
import { useLocation, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { trackConversion } from "@/lib/analytics";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserCircle, Phone, CalendarRange, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const ReservationSuccess = () => {
  const location = useLocation();
  const [reservationData, setReservationData] = useState(null);
  
  useEffect(() => {
    // Get reservation data from location state
    if (location.state?.reservation) {
      setReservationData(location.state.reservation);
      const r = location.state.reservation;
      trackConversion("purchase", { transaction_id: String(r.id ?? r.reservation_number ?? ""), value: Number(r.total_price ?? r.total_amount ?? 0) || undefined });
    }
  }, [location]);

  // Redirect if no reservation data
  if (!location.state?.reservation) {
    return <Navigate to="/reserver" replace />;
  }

  // Fetch admin contacts
  const { data: admins, isLoading } = useQuery({
    queryKey: ['contact-admins'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('display_on_contact', true)
        .eq('role', 'admin');
        
      if (error) throw error;
      return data;
    },
  });

  const reservation = location.state.reservation;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 bg-villa-sand/30 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Card className="shadow-lg border-t-4 border-t-green-500">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl font-playfair text-villa-blue">
                  Demande de réservation confirmée
                </CardTitle>
                <p className="text-gray-600 mt-2">
                  Nous avons bien reçu votre demande de réservation. Un de nos agents vous contactera très prochainement.
                </p>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="bg-villa-sand/30 p-4 rounded-lg">
                  <h3 className="font-medium text-lg mb-3">Détails de votre séjour</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start space-x-3">
                      <CalendarRange className="h-5 w-5 text-villa-turquoise mt-0.5" />
                      <div>
                        <p className="font-medium">Dates</p>
                        <p>
                          Du {format(new Date(reservation.check_in), "d MMMM yyyy", { locale: fr })} au{" "}
                          {format(new Date(reservation.check_out), "d MMMM yyyy", { locale: fr })}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Users className="h-5 w-5 text-villa-turquoise mt-0.5" />
                      <div>
                        <p className="font-medium">Voyageurs</p>
                        <p>{reservation.guests} personne{reservation.guests > 1 ? "s" : ""}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium text-lg mb-3">Nos contacts</h3>
                  
                  {isLoading ? (
                    <div className="text-center py-4">
                      <p className="text-gray-500">Chargement des contacts...</p>
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      {admins?.map((admin) => (
                        <Card key={admin.id} className="overflow-hidden">
                          <CardContent className="flex items-center space-x-4 p-4">
                            <Avatar className="w-16 h-16">
                              <AvatarImage 
                                src={admin.profile_image ? 
                                  supabase.storage
                                    .from('profile_images')
                                    .getPublicUrl(admin.profile_image)
                                    .data.publicUrl 
                                  : undefined
                                } 
                              />
                              <AvatarFallback>
                                <UserCircle className="w-8 h-8" />
                              </AvatarFallback>
                            </Avatar>
                            
                            <div>
                              <h3 className="font-medium">
                                {admin.first_name} {admin.last_name}
                              </h3>
                              
                              {admin.phone && (
                                <div className="flex items-center text-sm text-gray-600 mt-1">
                                  <Phone className="w-4 h-4 mr-1" />
                                  {admin.phone}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <p className="text-blue-800">
                    Nous vous remercions pour votre réservation. Un email de confirmation a été envoyé à l'adresse {reservation.email}.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ReservationSuccess;
